/**
 * Pagina dinámicamente el cuerpo de cláusulas (HTML editable) en páginas tamaño
 * carta (612x792), respetando los límites de bloque (no corta una cláusula a la
 * mitad). Cada página se renderiza como `.contrato-page` para que la generación
 * de PDF existente (que recorre `.contrato-page`) siga funcionando igual.
 *
 * El bloque de firmas (que recibe por prop, con datos automáticos) se coloca en la
 * ÚLTIMA página de cláusulas si cabe en el espacio sobrante; solo pasa a una página
 * aparte cuando no cabe. Así no se desperdicia una página completa.
 */
import React, { useEffect, useState, useRef } from 'react';

// Altura útil aproximada para cláusulas dentro de una página (px).
// 792 - encabezado(44) - padding-top cláusulas(52) - footer(28) - margen seguridad.
const ALTURA_UTIL = 640;

const Header = () => (
  <div className="doc-header">
    <img src="/logo.png" alt="TUVI" className="doc-logo" />
    <div className="doc-slogan">Facilidad • Seguridad • Calidad</div>
  </div>
);

const Footer = () => (
  <div className="doc-footer">
    <span className="footer-empresa">Tuvi Construcciones S.A.S</span>
    <div className="footer-contacto">
      <span>tuviconstrucciones.com</span>
      <span>@tuviacabados</span>
    </div>
  </div>
);

export default function ClausulasPaginadas({ html, firmaContent }) {
  const [paginas, setPaginas] = useState([]);
  const [firmasEnUltima, setFirmasEnUltima] = useState(false);
  const firmasMedRef = useRef(null);

  useEffect(() => {
    if (!html) { setPaginas([]); setFirmasEnUltima(false); return; }

    // 1) Parsear el HTML en bloques de primer nivel (elementos)
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const bloques = Array.from(tmp.children);
    if (bloques.length === 0) { setPaginas([html]); setFirmasEnUltima(false); return; }

    // 2) Contenedor de medición fuera de pantalla (ancho contenido = 612 - 160 = 452px)
    const meas = document.createElement('div');
    meas.style.cssText =
      'position:absolute;left:-99999px;top:0;visibility:hidden;width:452px;' +
      "font-family:'Segoe UI',Arial,sans-serif;font-size:9px;line-height:1.55;text-align:justify;";
    document.body.appendChild(meas);

    // 3) Acumular bloques por página hasta superar la altura útil
    const result = [];
    for (const bloque of bloques) {
      meas.appendChild(bloque.cloneNode(true));
      if (meas.offsetHeight > ALTURA_UTIL && meas.childNodes.length > 1) {
        const ultimo = meas.lastChild;
        meas.removeChild(ultimo);
        result.push(meas.innerHTML);
        meas.innerHTML = '';
        meas.appendChild(ultimo); // el bloque que no cupo abre la nueva página
      }
    }
    // Altura de la última página de cláusulas (aún en `meas`)
    const ultimaAltura = meas.offsetHeight;
    if (meas.childNodes.length) result.push(meas.innerHTML);
    document.body.removeChild(meas);

    // 4) ¿Caben las firmas en el espacio sobrante de la última página?
    const firmasH = firmasMedRef.current ? firmasMedRef.current.offsetHeight : 300;
    const cabe = result.length > 0 && (ultimaAltura + firmasH + 24) <= ALTURA_UTIL;

    setPaginas(result);
    setFirmasEnUltima(cabe);
  }, [html]);

  return (
    <>
      {/* Medición oculta de la altura del bloque de firmas */}
      <div
        ref={firmasMedRef}
        className="contrato-content contrato-clausulas"
        style={{ position: 'absolute', left: '-99999px', top: 0, visibility: 'hidden', width: 612, paddingTop: 0 }}
        aria-hidden="true"
      >
        {firmaContent}
      </div>

      {paginas.map((pagHtml, i) => {
        const esUltima = i === paginas.length - 1;
        return (
          <div className="contrato-preview contrato-page" key={`cl-${i}`}>
            <Header />
            <div
              className="contrato-content contrato-clausulas"
              dangerouslySetInnerHTML={{ __html: pagHtml }}
            />
            {esUltima && firmasEnUltima && (
              <div className="contrato-content contrato-clausulas" style={{ paddingTop: 0 }}>
                {firmaContent}
              </div>
            )}
            <Footer />
          </div>
        );
      })}

      {/* Página de firmas SOLO si no cupieron en la última página de cláusulas */}
      {!firmasEnUltima && (
        <div className="contrato-preview contrato-page">
          <Header />
          <div className="contrato-content contrato-clausulas">
            {firmaContent}
          </div>
          <Footer />
        </div>
      )}
    </>
  );
}
